import { ChangeEvent, useMemo } from "react";
import { useGetCertificatesQuery } from "../../../common/api/certificate";
import { CertificateStatusEnum } from "../../../common/enums/CertificateStatusEnum";
import { SortEnum } from "../../../common/enums/SortEnum";
import { useEstablishmentId } from "../../../common/utils/useEstablishmentId";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
    setAchievedCertificatesCurrentPage,
    setAchievedCertificatesItemsPerPage,
} from "../DashboardSlice";
import { mapAchievedCertificatesDataToRows } from "../utils/mapAchievedCertificatesDataToRows";
import { TableWidgetRow } from "@hatch-digital-nl/abs-storybook/legacy";

type UseDashboardAchievedCertificatesResult = {
    loading: boolean;
    table: {
        columns: string[];
        rows: TableWidgetRow[];
        total: number;
    };
    currentPage: number;
    itemsPerPage: number;
    itemsPerPageSelect: { value: number; activeValue: boolean }[];
    onPageChange: (page: number) => void;
    onItemsPerPageChange: (event: ChangeEvent<HTMLSelectElement>) => void;
    title: string;
    isEmpty: boolean;
};

export const useDashboardAchievedCertificates =
    (): UseDashboardAchievedCertificatesResult => {
        const dispatch = useAppDispatch();
        const {
            achievedCertificates: {
                currentPage,
                itemsPerPage,
                itemsPerPageSelect,
            },
        } = useAppSelector((state) => state.dashboard);

        const establishmentId = useEstablishmentId();

        const { data, isLoading, isFetching } = useGetCertificatesQuery({
            filter: {
                status: CertificateStatusEnum.ACTIVE,
                establishment_id: establishmentId,
            },
            page: currentPage,
            items_per_page: itemsPerPage,
            sort: SortEnum.QUALIFICATION_NAME_ASC,
        });

        const loading = isLoading || isFetching;

        const table = useMemo(() => {
            return {
                columns: ["Naam", ""] as string[],
                rows: mapAchievedCertificatesDataToRows({
                    achievedCertificates: data?.data ?? [],
                }),
                total: data?.meta?.total ?? 0,
            };
        }, [data]);

        const onPageChange = (page: number): void => {
            dispatch(setAchievedCertificatesCurrentPage(page));
        };

        const onItemsPerPageChange = (
            event: ChangeEvent<HTMLSelectElement>,
        ): void => {
            dispatch(
                setAchievedCertificatesItemsPerPage(Number(event.target.value)),
            );
        };

        const title = `Behaalde certificaten (${data?.meta?.total ?? 0})`;
        const isEmpty =
            !loading && !(data && data.data && data.data.length && data.meta);

        return {
            loading,
            table,
            currentPage,
            itemsPerPage,
            itemsPerPageSelect,
            onPageChange,
            onItemsPerPageChange,
            title,
            isEmpty,
        };
    };
